import Papa from 'papaparse';

export type CourseListDirectoryItem = {
  id: number | string;
  name: string;
  description: string;
  courseCount: number;
  code?: string;
  official?: boolean;
  courseIds: string[];
};

export function displayCourseListCode(item: CourseListDirectoryItem): string | null {
  const code = item.code?.trim();
  if (!code) return null;
  const coreCode = code.match(/(?:^|_)core_([a-z0-9-]{2,8})$/i)?.[1];
  if (coreCode) return coreCode.toUpperCase();
  return /^[A-Z0-9-]{2,8}$/.test(code) ? code : null;
}

export function parseCourseListDirectory(csv: string): CourseListDirectoryItem[] {
  const rows = Papa.parse<Record<string, string>>(csv, { header: true, skipEmptyLines: true }).data;
  return rows.flatMap((row) => {
    const id = Number(row.id);
    const name = String(row.name || '').trim();
    if (!Number.isFinite(id) || !name || String(row.is_public).toLowerCase() === 'false') return [];
    const courseIds = String(row.classes || '').match(/\d{8}/g) || [];
    return [{ id, name, description: String(row.description || '').trim(), courseCount: courseIds.length, courseIds }];
  });
}

export function parseDegreeCourseListDirectory(csv: string): CourseListDirectoryItem[] {
  const rows = Papa.parse<Record<string, string>>(csv, { header: true, skipEmptyLines: true }).data;
  return rows.flatMap((row) => {
    const code = String(row.name || '').trim();
    const name = String(row.clean_name || code).trim();
    if (!code || !name) return [];
    let courseIds: string[] = [];
    try {
      const classes = JSON.parse(String(row.classes || '[]'));
      courseIds = Array.isArray(classes) ? classes.map(String).map((value) => value.replace(/\D/g, '')).filter(Boolean) : [];
    } catch {
      courseIds = String(row.classes || '').match(/\d{8}/g) || [];
    }
    return [{ id: `degree_${code}`, code, name, description: String(row.description || '').trim(), courseCount: courseIds.length, courseIds, official: true }];
  });
}

export function searchCourseListDirectory(items: CourseListDirectoryItem[], query: string, limit = 6) {
  const normalized = query.trim().toLocaleLowerCase();
  if (normalized.length < 2) return [];
  const ranked = items
    .map((item) => {
      const name = item.name.toLocaleLowerCase();
      const description = item.description.toLocaleLowerCase();
      const code = item.code?.toLocaleLowerCase() || '';
      const isCompactCodeQuery = normalized.length <= 5 && !/\s/.test(normalized);
      const rank = code === normalized ? 0 : name === normalized ? 0 : code.includes(normalized) ? 1 : name.startsWith(normalized) ? 1 : name.includes(normalized) ? 2 : !isCompactCodeQuery && description.includes(normalized) ? 3 : 99;
      return { item, rank };
    })
    .filter(({ rank }) => rank < 99)
    .sort((left, right) => left.rank - right.rank || Number(right.item.official) - Number(left.item.official) || left.item.name.localeCompare(right.item.name));
  const seenNames = new Set<string>();
  return ranked.flatMap(({ item }) => {
    const key = item.name.trim().toLocaleLowerCase();
    if (seenNames.has(key)) return [];
    seenNames.add(key);
    return [item];
  }).slice(0, Math.max(1, Math.min(limit, 12)));
}
